import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

const T = {
  cream: "#F5EFE0", creamDark: "#EDE4D0", creamMid: "#E8DEC8",
  sand: "#D4C4A0", sandDark: "#B8A882",
  gold: "#C9A84C", goldLight: "#E8C96A", goldDark: "#9A7A2E",
  green: "#2D5A3D", greenLight: "#3D7A54", greenDark: "#1E3D29", greenPale: "#D4E8DA",
  text: "#2C2416", textMid: "#5C4D38", textMuted: "#8C7D68",
  white: "#FDFAF4", border: "rgba(180,155,100,0.25)", borderStrong: "rgba(180,155,100,0.45)",
  red: "#9b3030", redBg: "rgba(155,48,48,.08)", redBorder: "rgba(155,48,48,.28)",
};

const FD = "'Playfair Display', Georgia, serif";
const FB = "'DM Sans', 'Helvetica Neue', sans-serif";

/* ── API ── !NEW — real backend integration */
const API = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";
const getToken = () =>
  localStorage.getItem("dar_oulmes_token") ||
  sessionStorage.getItem("dar_oulmes_token") ||
  localStorage.getItem("access_token") ||
  "";

async function apiFetch(path, opts = {}) {
  const headers = { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json", ...opts.headers };
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  if (res.status === 204) return null;
  return res.json();
}

const api = {
  myDeliveries: (status) => apiFetch(`/api/delivery/orders/me${status ? `?status=${status}` : ""}`),
  updateStatus: (orderId, deliveryStatus, extra = {}) =>
    apiFetch(`/api/orders/${orderId}/delivery-status`, {
      method: "PUT",
      body: JSON.stringify({ deliveryStatus, ...extra }),
    }),
};

/* ── Load logged-in user from storage (populated at login) ── !NEW */
function loadUser() {
  try {
    return (
      JSON.parse(localStorage.getItem("dar_oulmes_user") || "null") ||
      JSON.parse(localStorage.getItem("user") || "null") ||
      null
    );
  } catch { return null; }
}


/* ── Order item helpers ── !NEW
   The backend can save cart items with slightly different shapes depending on
   where they were created: item.name, item.product.name, item.productName,
   item.qty, item.quantity, item.unitPrice, etc. These helpers make the
   delivery dashboard resilient and always readable for the delivery person. */
function money(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
}
function getOrderItemName(item = {}) {
  return (
    item.name ||
    item.title ||
    item.productName ||
    item.product?.name ||
    item.product?.title ||
    item.variantName ||
    "Product"
  );
}
function getOrderItemQty(item = {}) {
  const q = Number(item.quantity ?? item.qty ?? item.count ?? 1);
  return Number.isFinite(q) && q > 0 ? q : 1;
}
function getOrderItemPrice(item = {}) {
  return money(
    item.price ??
    item.unitPrice ??
    item.salePrice ??
    item.product?.price ??
    item.product?.salePrice ??
    0
  );
}
function getOrderItemTotal(item = {}) {
  return money(item.total ?? item.lineTotal ?? getOrderItemPrice(item) * getOrderItemQty(item));
}
function getOrderItemImage(item = {}) {
  const img =
    item.image ||
    item.imageUrl ||
    item.thumbnail ||
    item.productImage ||
    item.product?.image ||
    item.product?.imageUrl ||
    item.product?.thumbnail ||
    item.product?.images?.[0]?.url ||
    item.product?.images?.[0];
  return typeof img === "string" ? img : "";
}
function getOrderItemMeta(item = {}) {
  return [
    item.size && `Taille: ${item.size}`,
    item.color && `Couleur: ${item.color}`,
    item.variant && `Variante: ${item.variant}`,
    item.category && `Catégorie: ${item.category}`,
    item.categoryName && `Catégorie: ${item.categoryName}`,
    item.sku && `SKU: ${item.sku}`,
  ].filter(Boolean).join(" · ");
}

/* ── Backend→UI order shape adapter ── !NEW
   Backend returns: { id, deliveryStatus, deliveryLat, deliveryLng, deliveryAddress,
                      user:{name,email}, orderData:{items,customer}, totalAmount,
                      createdAt, assignedAt, deliveryNote }
   UI expects:       { id, status, latitude, longitude, customerName, address,
                       items, value, time, notes, collection }                        */
function normalize(o) {
  const items = Array.isArray(o.orderData?.items) ? o.orderData.items : [];
  const customer = o.orderData?.customer || {};
  const customerName =
    customer.name ||
    o.user?.name ||
    o.user?.email ||
    "Customer";
  const address =
    o.deliveryAddress ||
    [customer.address, customer.city, customer.zip].filter(Boolean).join(", ") ||
    "—";
  // Rabat fallback when no coords are set (so the map modal can still render)
  const latitude  = typeof o.deliveryLat === "number" ? o.deliveryLat : 33.9716;
  const longitude = typeof o.deliveryLng === "number" ? o.deliveryLng : -6.8498;
  const createdAt = o.assignedAt || o.createdAt;
  const time = createdAt
    ? new Date(createdAt).toLocaleTimeString("fr-MA", { hour: "2-digit", minute: "2-digit" })
    : "—";
  // First item's category as collection hint
  const collection = items[0]?.category || items[0]?.categoryName || "—";
  return {
    raw: o, // keep original around for any deep access
    id: `ORD-${String(o.id).padStart(4, "0")}`,
    realId: o.id, // numeric ID for API calls
    status: UI_STATUS_OF[o.deliveryStatus] || "pending",  // UI bucket (pending/picked/delivered/failed)
    latitude, longitude,
    customerName,
    customerPhone: customer.phone || customer.tel || null,
    address,
    items,
    itemCount: items.length || 0,
    itemNames: items.map(getOrderItemName).filter(Boolean).join(", "),
    value: `${o.totalAmount ?? 0} MAD`,
    time,
    notes: o.deliveryNote || customer.note || "",
    collection,
  };
}

/* Status mapping — new enum → old UI concepts.
   The existing UI categorises into pending/picked/delivered. We keep that mapping
   so Pill/OrderRow rendering still works, while carrying the real status alongside.  */
const UI_STATUS_OF = {
  UNASSIGNED:       "pending",
  ASSIGNED:         "pending",
  ACCEPTED:         "pending",
  OUT_FOR_DELIVERY: "picked",
  DELIVERED:        "delivered",
  FAILED:           "failed",
};

const STATUS = {
  pending:   { label: "En attente",     color: "#7A5C1E", bg: "rgba(201,168,76,0.14)", border: "rgba(201,168,76,0.38)", dot: "#C9A84C" },
  picked:    { label: "En transit",     color: "#1E3D29", bg: "#D4E8DA",                border: "rgba(45,90,61,0.32)",    dot: "#3D7A54" },
  delivered: { label: "Livré",          color: "#5A3E1B", bg: "rgba(180,155,100,0.18)", border: "rgba(180,155,100,0.38)", dot: "#B8A882" },
  failed:    { label: "Échec",          color: T.red,      bg: T.redBg,                  border: T.redBorder,              dot: T.red    },
};

function haverDist(a,b,c,d){const R=6371,dL=((c-a)*Math.PI)/180,dN=((d-b)*Math.PI)/180,x=Math.sin(dL/2)**2+Math.cos((a*Math.PI)/180)*Math.cos((c*Math.PI)/180)*Math.sin(dN/2)**2;return(R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x))).toFixed(0);}

/* ── SVG icons ── */
const I={
  Pkg:({s=14})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  Pin:({s=13})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Truck:({s=13})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  Check:({s=13})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Clock:({s=12})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Map:({s=14})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
  X:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Nav:({s=12})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>,
  Route:({s=12})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 000-7h-11a3.5 3.5 0 010-7H15"/><circle cx="18" cy="5" r="3"/></svg>,
  Bell:({s=17})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
  Search:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Alert:({s=12})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
};

/* ── Nav icons ── */
const NavIcons={
  dashboard:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  orders:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
  inventory:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/></svg>,
  customers:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
  analytics:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
  marketing:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  settings:({s=15})=><svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
};

/* ── Hooks ──
   !FIX — real live tracking like Google Maps:
   - Uses watchPosition (continuous stream) instead of polling getCurrentPosition.
   - enableHighAccuracy for GPS-grade precision.
   - Records a breadcrumb "trail" of traveled positions (with de-duplication
     for tiny jitter moves < ~5 m) so we can draw where the driver has been.
   - Exposes heading + speed from the GPS when available.                     */
function useDriverLocation(){
  const [loc,setLoc]=useState({
    lat:33.9716, lng:-6.8498,
    heading:null, speed:null, accuracy:null, ts:null,
  });
  const [trail,setTrail]=useState([]); // [[lat,lng], ...] in order visited

  useEffect(()=>{
    if(!navigator.geolocation)return;

    const onPos=p=>{
      const { latitude:lat, longitude:lng, heading, speed, accuracy } = p.coords;
      const next={ lat, lng, heading, speed, accuracy, ts:p.timestamp };
      setLoc(next);
      setTrail(prev=>{
        const last=prev[prev.length-1];
        // Skip if < ~5 m moved (jitter) — haverDist returns km as string
        if(last && Number(haverDist(last[0],last[1],lat,lng))*1000 < 5) return prev;
        const trimmed=prev.length>500 ? prev.slice(-400) : prev; // cap memory
        return [...trimmed,[lat,lng]];
      });
    };
    const onErr=()=>{ /* silent — keep last known */ };
    const opts={ enableHighAccuracy:true, maximumAge:2000, timeout:15000 };

    // First immediate fix, then continuous watch
    navigator.geolocation.getCurrentPosition(onPos,onErr,opts);
    const id=navigator.geolocation.watchPosition(onPos,onErr,opts);
    return()=>navigator.geolocation.clearWatch(id);
  },[]);

  return { loc, trail };
}

/* Fetch a real road-following route between two coordinates.
   !FIX — precision upgrade:
   - overview=full + geometries=geojson + steps=true → max-resolution polyline
     (OSRM otherwise simplifies, causing the line to cut corners through
     buildings).
   - continue=straight disables detours for tiny route alternatives.
   - Falls back to a second OSRM mirror, then to a straight line, so the
     destination line always renders even when the first server hiccups.
   Returns { coords:[[lat,lng],...], distance(m), duration(s) }.             */
const ROUTE_ENDPOINTS = [
  "https://router.project-osrm.org/route/v1/driving",
  "https://routing.openstreetmap.de/routed-car/route/v1/driving",
];

async function fetchRoadRoute(from, to){
  const params = "overview=full&geometries=geojson&steps=true&continue_straight=true&alternatives=false";
  for (const base of ROUTE_ENDPOINTS){
    try{
      const url = `${base}/${from.lng},${from.lat};${to.lng},${to.lat}?${params}`;
      const ctrl = new AbortController();
      const t = setTimeout(()=>ctrl.abort(), 6000);
      const res = await fetch(url, { signal: ctrl.signal });
      clearTimeout(t);
      if(!res.ok) continue;
      const json = await res.json();
      const route = json.routes?.[0];
      if(!route) continue;
      return {
        coords: route.geometry.coordinates.map(([lng,lat])=>[lat,lng]),
        distance: route.distance,
        duration: route.duration,
      };
    }catch{ /* try next endpoint */ }
  }
  return null;
}

/* Perpendicular distance (in metres) from point P to segment AB — used to
   detect when the driver has actually left the planned route so we can
   replan, same as turn-by-turn nav apps.                                   */
function distToSegmentMeters(p, a, b){
  const toM = (lat) => 111320 * Math.cos(lat * Math.PI/180);
  const lat0 = (a[0]+b[0])/2;
  const mx = toM(lat0);
  const ax = a[1]*mx,  ay = a[0]*110540;
  const bx = b[1]*mx,  by = b[0]*110540;
  const px = p[1]*mx,  py = p[0]*110540;
  const dx = bx-ax, dy = by-ay;
  const len2 = dx*dx + dy*dy;
  if (len2 === 0) return Math.hypot(px-ax, py-ay);
  let t = ((px-ax)*dx + (py-ay)*dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = ax + t*dx, qy = ay + t*dy;
  return Math.hypot(px-qx, py-qy);
}
function minDistToPolylineMeters(p, line){
  if (!line || line.length < 2) return Infinity;
  let m = Infinity;
  for (let i=0; i<line.length-1; i++){
    const d = distToSegmentMeters(p, line[i], line[i+1]);
    if (d < m) m = d;
  }
  return m;
}

/* !NEW — real backend hook.
   Fetches current delivery user's orders, maintains per-order loading state,
   and exposes an `update(realId, deliveryStatus, extra)` mutator that hits
   PUT /api/orders/:id/delivery-status and then refreshes the list.            */
function useOrders(){
  const [raw,setRaw]=useState([]);
  const [loading,setLoading]=useState({});   // per-order loading: { [orderId]: bool }
  const [fetching,setFetching]=useState(true);
  const [error,setError]=useState(null);

  const reload=useCallback(async()=>{
    setFetching(true); setError(null);
    try{ const r=await api.myDeliveries(); setRaw(r?.data||[]); }
    catch(e){ setError(e.message); }
    finally{ setFetching(false); }
  },[]);

  useEffect(()=>{ reload(); },[reload]);

  // Map to UI shape once, recompute when backend data changes
  const orders = useMemo(()=>raw.map(normalize), [raw]);

  const update = useCallback(async (realId, deliveryStatus, extra={}) => {
    setLoading(p=>({...p, [realId]:true}));
    try{
      await api.updateStatus(realId, deliveryStatus, extra);
      await reload();
    }catch(e){
      // Surface the error. A toast system would be nicer; alert is fine for now.
      alert(e.message);
    }finally{
      setLoading(p=>({...p, [realId]:false}));
    }
  },[reload]);

  return { orders, update, loading, fetching, error, reload };
}

/* ── Status pill ── */
function Pill({status}){
  const { t } = useTranslation();
  const m=STATUS[status] || STATUS.pending;
  const labelKey = { pending: "delivery.status_pending", picked: "delivery.status_picked", delivered: "delivery.status_delivered", failed: "delivery.status_failed" }[status] || "delivery.status_pending";
  return <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px 3px 8px",borderRadius:20,background:m.bg,border:`1px solid ${m.border}`,color:m.color,fontSize:11,fontWeight:600,fontFamily:FB,letterSpacing:"0.04em",whiteSpace:"nowrap"}}>
    <span style={{width:6,height:6,borderRadius:"50%",background:m.dot,flexShrink:0}}/>
    {t(labelKey)}
  </span>;
}

/* ── Google-Maps-style tile layers ──
   !NEW — actual Google tile servers (same ones Google's own site uses).
   These are keyless public tiles; each sub-domain (mt0..mt3) is load-balanced.
   - lyrs=m   → Roadmap (classic Google Maps look)
   - lyrs=s   → Satellite imagery
   - lyrs=y   → Hybrid (satellite + road labels baked in)
   - lyrs=p   → Terrain with roads
   We also keep a Carto "Voyager" option as a polished alternative styled very
   closely to modern Google Maps (soft beige roads, muted landscape).         */
const MAP_STYLES = {
  roadmap:   { url: "https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",     subs: "0123", attr: "© Google",      max: 20, label: "Plan"       },
  satellite: { url: "https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",     subs: "0123", attr: "© Google",      max: 20, label: "Satellite"  },
  hybrid:    { url: "https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",     subs: "0123", attr: "© Google",      max: 20, label: "Hybride"    },
  terrain:   { url: "https://mt{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}",     subs: "0123", attr: "© Google",      max: 20, label: "Terrain"    },
};

/* ── Map Modal ──
   !FIX — live Google-Maps-style tracking:
   - Google tile layers with a style switcher (Plan / Satellite / Hybride / Terrain)
   - Driver marker moves as GPS updates (refs, not re-created).
   - Route follows actual roads via OSRM (re-fetched every 15 s, or when the
     driver strays > 25 m from the planned polyline).
   - White route casing under the coloured line for sharp readability on any tile style.
   - Breadcrumb trail shows where the driver has actually been.
   - Auto-follow keeps the driver on-screen; user can toggle it off to pan
     around freely, and a "Recentrer" button snaps back.                      */
function MapModal({order,driverLoc,driverTrail,driverName,onClose}){
  const { t } = useTranslation();
  const mapRef=useRef(null);
  const inst=useRef(null);
  const driverMarkerRef=useRef(null);
  const accuracyCircleRef=useRef(null);
  const routeLineRef=useRef(null);       // main coloured route line
  const routeCasingRef=useRef(null);     // white outline under route (Google Maps technique)
  const trailLineRef=useRef(null);
  const tileLayerRef=useRef(null);       // current base-tile layer (swappable)
  const labelsLayerRef=useRef(null);     // hybrid/labels overlay
  const LRef=useRef(null);
  const routeCoordsRef=useRef([]);       // latest planned route (for off-route check)
  const replanInFlightRef=useRef(false); // prevents overlapping re-plans

  const [follow,setFollow]=useState(true);
  const [routeInfo,setRouteInfo]=useState(null); // { distance(m), duration(s) }
  const [ready,setReady]=useState(false);
  const [mapStyle,setMapStyle]=useState("roadmap"); // roadmap | satellite | hybrid | terrain

  // Keep latest values accessible inside async callbacks without re-mounting
  const driverLocRef=useRef(driverLoc);
  const followRef=useRef(follow);
  useEffect(()=>{ driverLocRef.current=driverLoc; },[driverLoc]);
  useEffect(()=>{ followRef.current=follow; },[follow]);

  /* Build map once */
  useEffect(()=>{
    if(!document.getElementById("lf-css")){
      const l=document.createElement("link");l.id="lf-css";l.rel="stylesheet";
      l.href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(l);
    }
    const load=()=>new Promise(res=>{
      if(window.L){res(window.L);return;}
      const s=document.createElement("script");
      s.src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      s.onload=()=>res(window.L);document.head.appendChild(s);
    });

    let cancelled=false;
    load().then(L=>{
      if(cancelled || inst.current) return;
      LRef.current=L;

      const map=L.map(mapRef.current,{center:[driverLoc.lat,driverLoc.lng],zoom:15,zoomControl:false,preferCanvas:true});
      L.control.zoom({position:"bottomright"}).addTo(map);

      // Google-Maps tile layer (default: Plan/Roadmap)
      const s0 = MAP_STYLES.roadmap;
      tileLayerRef.current = L.tileLayer(s0.url, {
        subdomains: s0.subs,
        maxZoom: s0.max,
        attribution: s0.attr,
        crossOrigin: true,
      }).addTo(map);

      // User dragging the map turns auto-follow off (like Google Maps)
      map.on("dragstart",()=>setFollow(false));

      inst.current=map;

      const mkDriver=L.divIcon({html:`<div style="width:38px;height:38px;border-radius:50%;background:${T.green};border:3px solid ${T.cream};display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 6px rgba(45,90,61,0.18),0 4px 12px rgba(0,0,0,0.22);font-size:17px;">🚗</div>`,className:"",iconSize:[38,38],iconAnchor:[19,19]});
      const mkDest=L.divIcon({html:`<div style="width:34px;height:34px;border-radius:50%;background:${T.gold};border:3px solid ${T.cream};display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 6px rgba(201,168,76,0.18),0 4px 12px rgba(0,0,0,0.22);font-size:15px;">📦</div>`,className:"",iconSize:[34,34],iconAnchor:[17,17]});

      const popStyle=`font-family:${FB};background:${T.cream};padding:10px 14px;border-radius:10px;min-width:140px;`;

      // Accuracy halo (updated whenever GPS reports new accuracy)
      accuracyCircleRef.current=L.circle([driverLoc.lat,driverLoc.lng],{
        radius:driverLoc.accuracy||25, color:T.green, weight:1, opacity:0.35,
        fillColor:T.green, fillOpacity:0.08, interactive:false,
      }).addTo(map);

      driverMarkerRef.current=L.marker([driverLoc.lat,driverLoc.lng],{icon:mkDriver})
        .bindPopup(`<div style="${popStyle}"><div style="font-weight:700;color:${T.green};font-size:13px">📍 ${t("delivery.your_position")}</div><div style="font-size:11px;color:${T.textMid};margin-top:2px">${driverName}</div></div>`,{className:"do-pp"})
        .addTo(map);

      L.marker([order.latitude,order.longitude],{icon:mkDest})
        .bindPopup(`<div style="${popStyle}"><div style="font-weight:700;color:${T.goldDark};font-size:12px;letter-spacing:0.04em">${order.id}</div><div style="font-size:14px;font-weight:700;color:${T.text};margin:3px 0">${order.customerName}</div><div style="font-size:11px;color:${T.textMid}">${order.address}</div></div>`,{className:"do-pp"})
        .openPopup().addTo(map);

      // Route rendered as two stacked polylines: a white "casing" underneath
      // plus the coloured line on top — the exact trick Google Maps uses to
      // keep the route crisp and legible over any basemap style.
      routeCasingRef.current = L.polyline([], {
        color: "#FFFFFF", weight: 9, opacity: 0.95,
        lineCap: "round", lineJoin: "round",
      }).addTo(map);
      routeLineRef.current = L.polyline([], {
        color: "#1A73E8",       // Google Maps directions blue
        weight: 6, opacity: 1,
        lineCap: "round", lineJoin: "round",
      }).addTo(map);
      trailLineRef.current = L.polyline([], {
        color: T.goldDark, weight: 4, opacity: 0.6,
        dashArray: "2,8", lineCap: "round",
      }).addTo(map);

      map.fitBounds([[driverLoc.lat,driverLoc.lng],[order.latitude,order.longitude]],{padding:[70,70]});
      setReady(true);
    });

    return()=>{
      cancelled=true;
      if(inst.current){inst.current.remove();inst.current=null;}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  /* Live driver marker + accuracy circle + auto-follow — runs on every GPS tick */
  useEffect(()=>{
    if(!ready || !driverMarkerRef.current) return;
    const ll=[driverLoc.lat,driverLoc.lng];
    driverMarkerRef.current.setLatLng(ll);
    if(accuracyCircleRef.current){
      accuracyCircleRef.current.setLatLng(ll);
      if(driverLoc.accuracy) accuracyCircleRef.current.setRadius(driverLoc.accuracy);
    }
    if(followRef.current && inst.current){
      inst.current.panTo(ll,{animate:true,duration:0.6});
    }

    // !FIX — keep the route anchored to the live driver position between
    // re-plans so the line is always precise (no lag between the driver
    // marker and the start of the route).
    const coords = routeCoordsRef.current;
    if (coords && coords.length >= 2){
      // Trim any leading points the driver has already passed, then prepend
      // the current driver position so the line starts exactly under the pin.
      let cutIdx = 0;
      let best = Infinity;
      for (let i=0; i<Math.min(coords.length, 12); i++){
        const d = Math.hypot(coords[i][0]-ll[0], coords[i][1]-ll[1]);
        if (d < best){ best = d; cutIdx = i; }
      }
      const trimmed = [ll, ...coords.slice(cutIdx+1)];
      if (routeCasingRef.current) routeCasingRef.current.setLatLngs(trimmed);
      if (routeLineRef.current)   routeLineRef.current.setLatLngs(trimmed);

      // If driver is > 25 m from the planned line, kick an immediate re-plan
      const drift = minDistToPolylineMeters(ll, coords);
      if (drift > 25 && !replanInFlightRef.current){
        replanInFlightRef.current = true;
        const dest = { lat: order.latitude, lng: order.longitude };
        fetchRoadRoute({ lat: ll[0], lng: ll[1] }, dest).then(r=>{
          if (r){
            const c = [ll, ...r.coords, [dest.lat, dest.lng]];
            routeCoordsRef.current = c;
            if (routeCasingRef.current) routeCasingRef.current.setLatLngs(c);
            if (routeLineRef.current)   routeLineRef.current.setLatLngs(c);
            setRouteInfo({ distance: r.distance, duration: r.duration });
          }
        }).finally(()=>{ replanInFlightRef.current = false; });
      }
    }
  },[driverLoc,ready,order.latitude,order.longitude]);

  /* !NEW — swap the base tile layer whenever mapStyle changes */
  useEffect(()=>{
    if(!ready || !inst.current || !LRef.current) return;
    const L=LRef.current;
    const s=MAP_STYLES[mapStyle]||MAP_STYLES.roadmap;
    if(tileLayerRef.current){
      inst.current.removeLayer(tileLayerRef.current);
    }
    if(labelsLayerRef.current){
      inst.current.removeLayer(labelsLayerRef.current);
      labelsLayerRef.current=null;
    }
    tileLayerRef.current=L.tileLayer(s.url,{
      subdomains:s.subs, maxZoom:s.max, attribution:s.attr, crossOrigin:true,
    }).addTo(inst.current);
    // Keep tiles under the route/markers
    tileLayerRef.current.getContainer && tileLayerRef.current.getContainer()
      && (tileLayerRef.current.getContainer().style.zIndex = 1);
  },[mapStyle,ready]);

  /* Live breadcrumb trail */
  useEffect(()=>{
    if(!ready || !trailLineRef.current) return;
    trailLineRef.current.setLatLngs(driverTrail);
  },[driverTrail,ready]);

  /* Fetch the road-following route. Refreshes every 15 s and also when the
     driver drifts > 25 m from the current route (e.g. took a different turn). */
  useEffect(()=>{
    if(!ready) return;
    let alive=true;
    let timer=null;

    const dest={lat:order.latitude,lng:order.longitude};

    const applyLine=(coords)=>{
      if(!alive) return;
      routeCoordsRef.current = coords;
      if(routeCasingRef.current) routeCasingRef.current.setLatLngs(coords);
      if(routeLineRef.current)   routeLineRef.current.setLatLngs(coords);
    };

    const refresh=async()=>{
      const from=driverLocRef.current;
      const r=await fetchRoadRoute({lat:from.lat,lng:from.lng},dest);
      if(!alive) return;
      if(r){
        // Guarantee the line starts EXACTLY at the driver and ends EXACTLY at
        // the destination — OSRM snaps to the nearest road node which can
        // leave a tiny visible gap otherwise.
        const coords = [[from.lat,from.lng], ...r.coords, [dest.lat,dest.lng]];
        applyLine(coords);
        setRouteInfo({distance:r.distance,duration:r.duration});
      }else{
        // Fallback: straight line if routing fails on all providers
        applyLine([[from.lat,from.lng],[dest.lat,dest.lng]]);
      }
    };

    refresh();
    timer=setInterval(refresh,15000); // re-plan every 15 s like a nav app

    return()=>{ alive=false; if(timer) clearInterval(timer); };
  },[ready,order.latitude,order.longitude]);

  const recenter=()=>{
    if(!inst.current) return;
    setFollow(true);
    inst.current.fitBounds([[driverLoc.lat,driverLoc.lng],[order.latitude,order.longitude]],{padding:[70,70]});
  };

  // Prefer the real road distance from OSRM; fall back to straight-line
  const kmRoad = routeInfo ? (routeInfo.distance/1000).toFixed(1) : null;
  const kmStraight = haverDist(driverLoc.lat,driverLoc.lng,order.latitude,order.longitude);
  const distLabel = kmRoad ? `${kmRoad} km` : `~${kmStraight} km`;
  const etaLabel = routeInfo ? `${Math.max(1,Math.round(routeInfo.duration/60))} min` : "—";

  return(
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(44,36,22,0.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,backdropFilter:"blur(4px)"}}>
      <style>{`.do-pp .leaflet-popup-content-wrapper{background:${T.cream}!important;border:1.5px solid ${T.border}!important;box-shadow:0 10px 40px rgba(44,36,22,0.2)!important;border-radius:12px!important;padding:0!important;}.do-pp .leaflet-popup-content{margin:0!important;}.do-pp .leaflet-popup-tip{background:${T.cream}!important;}.do-pp .leaflet-popup-close-button{color:${T.textMuted}!important;top:7px!important;right:10px!important;font-size:15px!important;}.leaflet-control-zoom a{background:${T.white}!important;color:${T.text}!important;border-color:${T.border}!important;}@keyframes doPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(1.35)}}`}</style>

      <div style={{background:T.white,borderRadius:18,border:`1.5px solid ${T.border}`,boxShadow:"0 24px 70px rgba(44,36,22,0.26)",width:"min(820px,100%)",maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden",animation:"fadeSlide .22s ease"}}>

        {/* Header */}
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,background:T.cream,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:40,height:40,borderRadius:10,background:T.greenPale,display:"flex",alignItems:"center",justifyContent:"center",color:T.green}}><I.Map s={18}/></div>
            <div>
              <div style={{fontFamily:FD,fontSize:16,fontWeight:700,color:T.text,lineHeight:1.2}}>{t("delivery.map_title")}</div>
              <div style={{fontSize:12,color:T.textMuted,fontFamily:FB,marginTop:2}}>{order.id} · {order.customerName}</div>
            </div>
          </div>
          <button onClick={onClose} style={{width:34,height:34,borderRadius:8,background:"transparent",border:`1px solid ${T.border}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.textMid}}>
            <I.X s={14}/>
          </button>
        </div>

        {/* Info strip */}
        <div style={{padding:"11px 24px",background:T.creamMid,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:20,flexWrap:"wrap"}}>
          {[
            {icon:<I.Pin s={12}/>,label:t("delivery.address"),val:order.address},
            {icon:<I.Route s={12}/>,label:t("delivery.table_distance"),val:distLabel},
            {icon:<I.Clock s={12}/>,label:t("delivery.eta"),val:etaLabel},
            {icon:<I.Pkg s={12}/>,label:t("delivery.table_products"),val:t("delivery.articles_count", { count: order.itemCount || 0 })},
          ].map(it=>(
            <div key={it.label} style={{display:"flex",alignItems:"center",gap:5}}>
              <span style={{color:T.goldDark}}>{it.icon}</span>
              <span style={{fontSize:12,color:T.textMuted,fontFamily:FB}}>{it.label}:</span>
              <span style={{fontSize:12,fontWeight:600,color:T.text,fontFamily:FB}}>{it.val}</span>
            </div>
          ))}
          <Pill status={order.status}/>
          {/* Live indicator — blinks while watchPosition is streaming */}
          <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto",fontSize:11,fontFamily:FB,color:T.green,fontWeight:600}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:T.green,animation:"doPulse 1.4s ease-in-out infinite"}}/>
            {t("delivery.live_tracking")}
          </div>
        </div>

        {/* Map + style switcher overlay */}
        <div style={{position:"relative",flex:1,minHeight:380}}>
          <div ref={mapRef} style={{position:"absolute",inset:0}}/>

          {/* !NEW — Google-Maps-style layer picker (top-left of map) */}
          <div style={{position:"absolute",top:12,left:12,zIndex:500,background:T.white,borderRadius:10,border:`1px solid ${T.border}`,boxShadow:"0 4px 14px rgba(0,0,0,0.12)",padding:4,display:"flex",gap:2,fontFamily:FB}}>
            {Object.entries(MAP_STYLES).map(([key,s])=>{
              const active=mapStyle===key;
              return (
                <button
                  key={key}
                  onClick={()=>setMapStyle(key)}
                  title={s.label}
                  style={{
                    padding:"6px 11px",borderRadius:7,border:"none",cursor:"pointer",
                    fontSize:11,fontWeight:600,letterSpacing:"0.02em",
                    background:active?T.green:"transparent",
                    color:active?T.cream:T.textMid,
                    transition:"all .15s",
                  }}
                >{s.label}</button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{padding:"13px 24px",borderTop:`1px solid ${T.border}`,background:T.cream,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:11,color:T.textMuted,fontFamily:FB,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:"50%",background:T.green,display:"inline-block"}}/>{t("delivery.your_position")}</span>
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:"50%",background:T.gold,display:"inline-block"}}/>{t("delivery.destination")}</span>
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:18,height:4,background:"#1A73E8",display:"inline-block",borderRadius:2,boxShadow:"0 0 0 1.5px #fff"}}/>{t("delivery.route")}</span>
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:18,height:0,borderTop:`3px dotted ${T.goldDark}`,display:"inline-block"}}/>{t("delivery.traveled")}</span>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button
              onClick={recenter}
              title={t("delivery.recenter")}
              style={{padding:"8px 14px",borderRadius:9,background:follow?T.greenPale:T.white,border:`1px solid ${follow?T.green:T.border}`,color:follow?T.green:T.textMid,fontSize:12,fontWeight:600,fontFamily:FB,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}
            >
              <I.Nav s={12}/>{follow ? t("delivery.follow_active") : t("delivery.follow_recenter")}
            </button>
            <button onClick={onClose} style={{padding:"8px 22px",borderRadius:9,background:T.green,border:"none",color:T.cream,fontSize:13,fontWeight:600,fontFamily:FB,cursor:"pointer"}}>{t("delivery.close")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sidebar ── */
const NAV=[
  {id:"dashboard",labelKey:"nav.dashboard"},
  {id:"orders",labelKey:"dash.orders"},
  {id:"inventory",labelKey:"nav.delivery_panel"},
  {id:"customers",labelKey:"common.customer"},
  {id:"analytics",labelKey:"dash.overview"},
  {id:"marketing",labelKey:"nav.products"},
  {id:"settings",labelKey:"nav.settings"},
];
function Sidebar({active,setActive,user}){
  const { t } = useTranslation();
  const avatar = (user?.name || user?.email || "U").split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2);
  const displayName = user?.name || user?.email || t("delivery.default_driver");
  return(
    <aside style={{width:208,background:T.cream,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",flexShrink:0}}>
      <div style={{padding:"24px 20px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",flexDirection:"column",alignItems:"center",gap:7}}>
        <div style={{width:58,height:58,borderRadius:"50%",border:`2px solid ${T.gold}`,background:T.white,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>🏡</div>
        <div style={{textAlign:"center"}}>
          <div style={{fontFamily:FD,fontSize:14,fontWeight:700,color:T.green,letterSpacing:"0.08em"}}>{t("brand.name")}</div>
          <div style={{fontSize:9,color:T.textMuted,fontFamily:FB,letterSpacing:"0.12em",textTransform:"uppercase"}}>{t("delivery.express")}</div>
        </div>
      </div>
      <nav style={{flex:1,padding:"12px 10px",display:"flex",flexDirection:"column",gap:2}}>
        {NAV.map(({id,labelKey})=>{
          const isA=active===id;
          const Icon=NavIcons[id]||NavIcons.settings;
          return(
            <button key={id} onClick={()=>setActive(id)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:9,background:isA?T.green:"transparent",border:"none",cursor:"pointer",color:isA?T.cream:T.textMid,fontSize:13,fontFamily:FB,fontWeight:isA?600:400,textAlign:"left",transition:"all .15s",width:"100%"}}>
              <span style={{opacity:isA?1:.7,flexShrink:0}}><Icon s={15}/></span>{t(labelKey)}
            </button>
          );
        })}
      </nav>
      <div style={{padding:"13px 14px",borderTop:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:9}}>
        <div style={{width:34,height:34,borderRadius:"50%",background:T.green,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:T.cream,flexShrink:0}}>{avatar}</div>
        <div style={{minWidth:0}}>
          <div style={{fontSize:12,fontWeight:600,color:T.text,fontFamily:FB,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{displayName}</div>
          <div style={{fontSize:10,color:T.textMuted,fontFamily:FB}}>{t("delivery.driver")}</div>
        </div>
      </div>
    </aside>
  );
}

/* ── Stat card ── */
function Stat({label,value,color=T.text}){
  return(
    <div style={{background:T.white,borderRadius:12,border:`1px solid ${T.border}`,padding:"14px 18px",flex:"1 1 110px"}}>
      <div style={{fontSize:10,color:T.textMuted,fontFamily:FB,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.07em"}}>{label}</div>
      <div style={{fontSize:22,fontWeight:700,color,fontFamily:FD}}>{value}</div>
    </div>
  );
}


/* ── Product renderers ── !NEW */
function ProductThumb({ item, size = 36 }) {
  const img = getOrderItemImage(item);
  if (img) {
    return (
      <img
        src={img}
        alt={getOrderItemName(item)}
        style={{width:size,height:size,borderRadius:9,objectFit:"cover",border:`1px solid ${T.border}`,background:T.cream,flexShrink:0}}
        onError={(e)=>{ e.currentTarget.style.display="none"; }}
      />
    );
  }
  return (
    <div style={{width:size,height:size,borderRadius:9,background:T.cream,border:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"center",color:T.goldDark,flexShrink:0}}>
      <I.Pkg s={Math.max(13, Math.round(size * .45))}/>
    </div>
  );
}

function ProductsSummary({ order }) {
  const { t } = useTranslation();
  const items = Array.isArray(order.items) ? order.items : [];
  if (!items.length) {
    return <span style={{fontSize:12,color:T.textMuted,fontFamily:FB}}>{t("delivery.no_product")}</span>;
  }
  const visible = items.slice(0, 2);
  const extra = Math.max(0, items.length - visible.length);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:6,minWidth:220,maxWidth:340}}>
      {visible.map((item, idx)=>{
        const qty = getOrderItemQty(item);
        const price = getOrderItemPrice(item);
        return (
          <div key={`${getOrderItemName(item)}-${idx}`} style={{display:"flex",alignItems:"center",gap:8}}>
            <ProductThumb item={item} size={34}/>
            <div style={{minWidth:0,flex:1}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,fontFamily:FB,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}} title={getOrderItemName(item)}>
                {getOrderItemName(item)}
              </div>
              <div style={{fontSize:11,color:T.textMuted,fontFamily:FB,marginTop:1}}>
                {t("delivery.qty")} {qty}{price ? ` · ${price.toFixed(0)} MAD` : ""}
              </div>
            </div>
          </div>
        );
      })}
      {extra > 0 && (
        <div style={{fontSize:11,color:T.goldDark,fontFamily:FB,fontWeight:700,paddingLeft:42}}>{t(extra > 1 ? "delivery.more_products_plural" : "delivery.more_products", { count: extra })}</div>
      )}
    </div>
  );
}

function ProductsDetailList({ order }) {
  const { t } = useTranslation();
  const items = Array.isArray(order.items) ? order.items : [];
  if (!items.length) {
    return (
      <div style={{fontSize:12,color:T.textMuted,fontFamily:FB,background:T.cream,padding:"10px 12px",borderRadius:9,border:`1px solid ${T.border}`}}>
        {t("delivery.no_product_order")}
      </div>
    );
  }
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:10,width:"100%"}}>
      {items.map((item, idx)=>{
        const qty = getOrderItemQty(item);
        const price = getOrderItemPrice(item);
        const total = getOrderItemTotal(item);
        const meta = getOrderItemMeta(item);
        return (
          <div key={`${getOrderItemName(item)}-${idx}`} style={{display:"flex",gap:10,background:T.cream,padding:10,borderRadius:11,border:`1px solid ${T.border}`,alignItems:"flex-start"}}>
            <ProductThumb item={item} size={48}/>
            <div style={{minWidth:0,flex:1}}>
              <div style={{fontSize:13,fontWeight:800,color:T.text,fontFamily:FB,lineHeight:1.25}}>{getOrderItemName(item)}</div>
              {meta && <div style={{fontSize:10.5,color:T.textMuted,fontFamily:FB,marginTop:3,lineHeight:1.35}}>{meta}</div>}
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:7}}>
                <span style={{fontSize:11,fontWeight:700,color:T.greenDark,background:T.greenPale,border:`1px solid rgba(45,90,61,.2)`,padding:"3px 7px",borderRadius:999}}>{t("delivery.qty")}: {qty}</span>
                {price > 0 && <span style={{fontSize:11,fontWeight:700,color:T.goldDark,background:"rgba(201,168,76,.12)",border:`1px solid rgba(201,168,76,.25)`,padding:"3px 7px",borderRadius:999}}>{price.toFixed(0)} MAD / {t("delivery.unit")}</span>}
                {total > 0 && <span style={{fontSize:11,fontWeight:800,color:T.text,background:T.white,border:`1px solid ${T.border}`,padding:"3px 7px",borderRadius:999}}>{t("common.total")}: {total.toFixed(0)} MAD</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Order row ── */
function OrderRow({order,driverLoc,isSelected,onClick,onUpdate,loadingStatus,onMap}){
  const { t } = useTranslation();
  const dist=haverDist(driverLoc.lat,driverLoc.lng,order.latitude,order.longitude);
  const ds = order.raw?.deliveryStatus || "ASSIGNED"; // real backend status

  // !NEW — render action buttons based on the real delivery-status state machine
  const renderActions = () => {
    if (ds === "ASSIGNED") return (
      <button onClick={e=>{e.stopPropagation();onUpdate(order.realId,"ACCEPTED");}} disabled={loadingStatus}
        style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,fontFamily:FB,cursor:"pointer",background:"rgba(201,168,76,0.12)",border:`1px solid rgba(201,168,76,0.4)`,color:T.goldDark,display:"flex",alignItems:"center",gap:5,opacity:loadingStatus ? .6 : 1,whiteSpace:"nowrap"}}>
        <I.Check s={11}/>{loadingStatus ? "…" : t("delivery.accept")}
      </button>
    );
    if (ds === "ACCEPTED") return (
      <button onClick={e=>{e.stopPropagation();onUpdate(order.realId,"OUT_FOR_DELIVERY");}} disabled={loadingStatus}
        style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,fontFamily:FB,cursor:"pointer",background:"rgba(201,168,76,0.12)",border:`1px solid rgba(201,168,76,0.4)`,color:T.goldDark,display:"flex",alignItems:"center",gap:5,opacity:loadingStatus ? .6 : 1,whiteSpace:"nowrap"}}>
        <I.Truck s={11}/>{loadingStatus ? "…" : t("delivery.start")}
      </button>
    );
    if (ds === "OUT_FOR_DELIVERY") return (
      <>
        <button onClick={e=>{e.stopPropagation();onUpdate(order.realId,"DELIVERED");}} disabled={loadingStatus}
          style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,fontFamily:FB,cursor:"pointer",background:T.greenPale,border:`1px solid rgba(45,90,61,0.35)`,color:T.greenDark,display:"flex",alignItems:"center",gap:5,opacity:loadingStatus ? .6 : 1,whiteSpace:"nowrap"}}>
          <I.Check s={11}/>{loadingStatus ? "…" : t("delivery.delivered")}
        </button>
        <button onClick={e=>{
            e.stopPropagation();
            const reason = prompt(t("delivery.failure_reason"));
            if (!reason) return;
            onUpdate(order.realId,"FAILED",{failureReason:reason});
          }} disabled={loadingStatus}
          style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,fontFamily:FB,cursor:"pointer",background:T.redBg,border:`1px solid ${T.redBorder}`,color:T.red,display:"flex",alignItems:"center",gap:5,opacity:loadingStatus ? .6 : 1,whiteSpace:"nowrap"}}>
          <I.Alert s={11}/>{t("delivery.failed")}
        </button>
      </>
    );
    if (ds === "DELIVERED") return (
      <span style={{fontSize:11,color:T.sandDark,fontFamily:FB,display:"flex",alignItems:"center",gap:4}}><I.Check s={11}/>{t("delivery.delivered")}</span>
    );
    if (ds === "FAILED") return (
      <span style={{fontSize:11,color:T.red,fontFamily:FB,display:"flex",alignItems:"center",gap:4}}><I.Alert s={11}/>{t("delivery.failed")}</span>
    );
    return null;
  };

  return(
    <tr onClick={onClick} style={{background:isSelected?"rgba(201,168,76,0.07)":"transparent",borderBottom:`1px solid ${T.border}`,cursor:"pointer",transition:"background .15s"}}>
      <td style={{padding:"13px 0 13px 20px",width:36}}>
        <div style={{width:16,height:16,borderRadius:4,border:isSelected?`2px solid ${T.green}`:`1.5px solid ${T.sand}`,background:isSelected?T.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
          {isSelected&&<span style={{color:T.cream,fontSize:10}}>✓</span>}
        </div>
      </td>
      <td style={{padding:"13px 10px",width:90}}>
        <span style={{fontSize:11,fontWeight:700,color:T.goldDark,fontFamily:"'Space Mono',monospace",letterSpacing:"0.04em"}}>{order.id}</span>
      </td>
      <td style={{padding:"13px 10px",minWidth:155}}>
        <div style={{fontSize:13,fontWeight:600,color:T.text,fontFamily:FB}}>{order.customerName}</div>
        <div style={{fontSize:11,color:T.textMuted,display:"flex",alignItems:"center",gap:3,marginTop:2}}>
          <I.Pin s={10}/><span style={{maxWidth:155,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{order.address}</span>
        </div>
      </td>
      <td style={{padding:"13px 10px",minWidth:260}}>
        <ProductsSummary order={order}/>
      </td>
      <td style={{padding:"13px 10px",width:120}}><Pill status={order.status}/></td>
      <td style={{padding:"13px 10px",width:80}}>
        <span style={{fontSize:12,color:T.textMid,fontFamily:FB,display:"flex",alignItems:"center",gap:3}}><I.Nav s={11}/>{dist} km</span>
      </td>
      <td style={{padding:"13px 10px",width:90}}>
        <span style={{fontSize:13,fontWeight:700,color:T.green,fontFamily:FB}}>{order.value}</span>
      </td>
      <td style={{padding:"13px 10px",width:68}}>
        <span style={{fontSize:11,color:T.textMuted,fontFamily:FB,display:"flex",alignItems:"center",gap:3}}><I.Clock s={10}/>{order.time}</span>
      </td>
      <td style={{padding:"13px 20px 13px 10px",width:230}}>
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
          <button onClick={e=>{e.stopPropagation();onMap(order);}} title={t("delivery.view_map")} style={{width:30,height:30,borderRadius:7,background:T.greenPale,border:`1px solid rgba(45,90,61,0.25)`,color:T.green,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s",flexShrink:0}}>
            <I.Map s={13}/>
          </button>
          {renderActions()}
        </div>
      </td>
    </tr>
  );
}

/* ── Main ── */
export default function DeliveryDashboard(){
  const { t, i18n } = useTranslation();
  const { loc:driverLoc, trail:driverTrail } = useDriverLocation();
  const {orders,update,loading,fetching,error,reload}=useOrders();  // !NEW — real backend
  const [user] = useState(loadUser);                                 // !NEW — from storage
  const [selected,setSelected]=useState(null);
  const [mapOrder,setMapOrder]=useState(null);
  const [filter,setFilter]=useState("all");
  const [nav,setNav]=useState("inventory");
  const [search,setSearch]=useState("");
  const [time,setTime]=useState(new Date());

  useEffect(()=>{const t=setInterval(()=>setTime(new Date()),1000);return()=>clearInterval(t);},[]);

  const driverName = user?.name || user?.email || t("delivery.default_driver");
  const driverAvatar = (user?.name || user?.email || "U").split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2);

  const filtered=orders.filter(o=>{
    const mf=filter==="all"?true:o.status===filter;
    const ms=search===""||[o.customerName,o.id,o.address,o.itemNames].filter(Boolean).some(s=>String(s).toLowerCase().includes(search.toLowerCase()));
    return mf&&ms;
  });

  const counts={
    all:orders.length,
    pending:orders.filter(o=>o.status==="pending").length,
    picked:orders.filter(o=>o.status==="picked").length,
    delivered:orders.filter(o=>o.status==="delivered").length,
    failed:orders.filter(o=>o.status==="failed").length,
  };
  const earned=orders.filter(o=>o.status==="delivered").reduce((a,o)=>a+parseFloat(o.raw?.totalAmount||0),0);

  return(
    <div style={{fontFamily:FB,minHeight:"100vh",display:"flex",color:T.text,position:"relative",paddingTop:"90px"}}>
      {/* <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');*{box-sizing:border-box;margin:0;padding:0;}::-webkit-scrollbar{width:4px;height:4px;}::-webkit-scrollbar-thumb{background:${T.sand};border-radius:2px;}@keyframes fadeSlide{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}tr:hover{background:rgba(201,168,76,0.05)!important;}button:hover{opacity:.88;}`}</style> */}

      {/* <Sidebar active={nav} setActive={setNav} user={user}/> */}

      <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
        {/* Topbar */}
        <header style={{height:60,background:T.cream,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 28px",flexShrink:0}}>
          <div style={{position:"relative",flex:"0 1 340px"}}>
            <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:T.textMuted}}><I.Search s={14}/></span>
            <input type="text" placeholder={t("delivery.search_ph")} value={search} onChange={e=>setSearch(e.target.value)} style={{width:"100%",height:36,borderRadius:20,border:`1.5px solid ${T.border}`,background:T.white,padding:"0 16px 0 34px",fontSize:13,fontFamily:FB,color:T.text,outline:"none"}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <span style={{fontSize:12,color:T.textMuted,fontFamily:"'Space Mono',monospace"}}>{time.toLocaleTimeString(i18n.language === "ar" ? "ar-MA" : i18n.language === "en" ? "en-MA" : "fr-MA",{hour:"2-digit",minute:"2-digit"})}</span>
            <button onClick={reload} title={t("delivery.refresh")} style={{width:34,height:34,borderRadius:9,background:T.white,border:`1px solid ${T.border}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.textMid,fontSize:14}}>
              {fetching ? "…" : "⟳"}
            </button>
            <div style={{position:"relative"}}>
              <button style={{width:34,height:34,borderRadius:9,background:T.white,border:`1px solid ${T.border}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.textMid}}><I.Bell s={16}/></button>
              <span style={{position:"absolute",top:7,right:8,width:7,height:7,borderRadius:"50%",background:T.gold,border:`1.5px solid ${T.cream}`}}/>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:9}}>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:12,fontWeight:600,color:T.text}}>{driverName}</div>
                <div style={{fontSize:10,color:T.textMuted}}>{user?.email || t("delivery.default_driver")}</div>
              </div>
              <div style={{width:34,height:34,borderRadius:"50%",background:T.green,color:T.cream,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>{driverAvatar}</div>
            </div>
          </div>
        </header>

        {/* Body */}
        <div style={{flex:1,overflowY:"auto",padding:"26px 28px"}}>
          <div style={{marginBottom:20}}>
            <h1 style={{fontFamily:FD,fontSize:34,fontWeight:700,color:T.text,letterSpacing:"-0.01em",lineHeight:1}}>{t("delivery.title")}</h1>
            <p style={{fontSize:13,color:T.textMuted,marginTop:5}}>{new Date().toLocaleDateString(i18n.language === "ar" ? "ar-MA" : i18n.language === "en" ? "en-MA" : "fr-MA",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</p>
          </div>

          {/* !NEW — error banner */}
          {error && (
            <div style={{marginBottom:16,padding:"12px 16px",borderRadius:10,background:T.redBg,border:`1px solid ${T.redBorder}`,color:T.red,fontSize:13,fontFamily:FB}}>
              ⚠ {t("delivery.load_error", { error })}
            </div>
          )}

          {/* Stats */}
          <div style={{display:"flex",gap:12,marginBottom:22,flexWrap:"wrap"}}>
            <Stat label={t("delivery.stats_total")} value={counts.all} color={T.text}/>
            <Stat label={t("delivery.stats_pending")} value={counts.pending} color={T.goldDark}/>
            <Stat label={t("delivery.stats_picked")} value={counts.picked} color={T.greenLight}/>
            <Stat label={t("delivery.stats_delivered")} value={counts.delivered} color={T.green}/>
            <Stat label={t("delivery.stats_failed")} value={counts.failed} color={T.red}/>
            <Stat label={t("delivery.stats_earned")} value={`${earned.toFixed(0)} MAD`} color={T.greenDark}/>
          </div>

          {/* Table card */}
          <div style={{background:T.white,borderRadius:14,border:`1px solid ${T.border}`,overflow:"hidden"}}>
            {/* Toolbar */}
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${T.border}`,background:T.cream,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {[{k:"all",l:t("delivery.filter_all")},{k:"pending",l:t("delivery.filter_pending")},{k:"picked",l:t("delivery.filter_picked")},{k:"delivered",l:t("delivery.filter_delivered")},{k:"failed",l:t("delivery.filter_failed")}].map(f=>(
                  <button key={f.k} onClick={()=>setFilter(f.k)} style={{padding:"5px 13px",borderRadius:20,fontSize:12,fontFamily:FB,fontWeight:500,cursor:"pointer",border:filter===f.k?`1.5px solid ${T.green}`:`1px solid ${T.border}`,background:filter===f.k?T.green:"transparent",color:filter===f.k?T.cream:T.textMid,transition:"all .15s"}}>
                    {f.l}{counts[f.k]>0&&<span style={{marginLeft:5,fontSize:10,fontWeight:700,background:filter===f.k?"rgba(255,255,255,0.22)":T.sandDark+"22",color:filter===f.k?T.cream:T.textMid,padding:"1px 5px",borderRadius:10}}>{counts[f.k]}</span>}
                  </button>
                ))}
              </div>
              <span style={{fontSize:12,color:T.textMuted,fontFamily:FB}}>{filtered.length} {filtered.length !== 1 ? t("common.results") : t("common.result")}</span>
            </div>

            {/* Table */}
            <div style={{overflowX:"auto"}}>
              {fetching && orders.length===0 ? (
                <div style={{padding:"56px 20px",textAlign:"center",color:T.textMuted,fontFamily:FB,fontSize:13}}>
                  {t("delivery.loading")}
                </div>
              ) : filtered.length===0?(
                <div style={{padding:"56px 20px",display:"flex",flexDirection:"column",alignItems:"center",color:T.textMuted,fontFamily:FB}}>
                  <div style={{fontSize:36,marginBottom:12}}>📭</div>
                  <div style={{fontSize:15,fontWeight:600,color:T.textMid,marginBottom:4}}>{t("delivery.empty_title")}</div>
                  <div style={{fontSize:13}}>{t("delivery.empty_desc")}</div>
                </div>
              ):(
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{borderBottom:`1.5px solid ${T.border}`,textAlign:"center"}}>
                      {["",t("delivery.table_id"),t("delivery.table_customer"),t("delivery.table_products"),t("delivery.table_status"),t("delivery.table_distance"),t("delivery.table_value"),t("delivery.table_time"),t("delivery.table_actions")].map((h,i)=>(
                        <th key={i} style={{padding:h===""?"11px 0 11px 20px":"11px 10px",textAlign:"center",fontSize:10,fontFamily:FB,fontWeight:600,color:T.textMuted,letterSpacing:"0.07em",textTransform:"uppercase",background:T.cream,whiteSpace:"nowrap",...(h==="Actions"&&{paddingRight:20})}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(order=>(
                      <OrderRow key={order.realId} order={order} driverLoc={driverLoc} isSelected={selected?.realId===order.realId} onClick={()=>setSelected(p=>p?.realId===order.realId?null:order)} onUpdate={update} loadingStatus={loading[order.realId]} onMap={setMapOrder}/>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Selected detail */}
          {selected&&(
            <div style={{marginTop:14,background:T.white,borderRadius:12,border:`1.5px solid ${T.goldDark}44`,padding:"15px 20px",display:"flex",alignItems:"flex-start",gap:18,flexWrap:"wrap",animation:"fadeSlide .2s ease"}}>
              <div style={{flex:1,minWidth:160}}>
                <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{t("delivery.selected_order")}</div>
                <div style={{fontFamily:FD,fontSize:16,fontWeight:700,color:T.text}}>{selected.customerName}</div>
                <div style={{fontSize:12,color:T.textMuted,marginTop:3,display:"flex",alignItems:"center",gap:4}}><I.Pin s={11}/>{selected.address}</div>
                {selected.customerPhone && (
                  <a href={`tel:${selected.customerPhone}`} style={{fontSize:12,color:T.green,marginTop:4,display:"inline-flex",alignItems:"center",gap:4,textDecoration:"none"}}>
                    📞 {selected.customerPhone}
                  </a>
                )}
              </div>
              {selected.notes&&(
                <div style={{flex:1,minWidth:150}}>
                  <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{t("delivery.note")}</div>
                  <div style={{fontSize:12,color:T.goldDark,fontStyle:"italic",background:"rgba(201,168,76,0.08)",padding:"6px 10px",borderRadius:7,border:`1px solid rgba(201,168,76,0.2)`}}>{selected.notes}</div>
                </div>
              )}
              <div style={{width:"100%",borderTop:`1px solid ${T.border}`,paddingTop:14,marginTop:2}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:9,flexWrap:"wrap"}}>
                  <div>
                    <div style={{fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>{t("delivery.products_to_deliver")}</div>
                    <div style={{fontSize:13,fontWeight:800,color:T.text,fontFamily:FB}}>{selected.itemCount || 0} {(selected.itemCount || 0) !== 1 ? t("delivery.articles") : t("delivery.article")}</div>
                  </div>
                  <div style={{fontSize:13,fontWeight:800,color:T.green,fontFamily:FB}}>{t("delivery.selected_total", { value: selected.value })}</div>
                </div>
                <ProductsDetailList order={selected}/>
              </div>

              <button onClick={()=>setMapOrder(selected)} style={{padding:"9px 18px",borderRadius:9,background:T.green,border:"none",color:T.cream,fontSize:13,fontWeight:600,fontFamily:FB,cursor:"pointer",display:"flex",alignItems:"center",gap:7,alignSelf:"center"}}>
                <I.Map s={14}/>{t("delivery.view_map")}
              </button>
            </div>
          )}
        </div>
      </div>

      {mapOrder&&<MapModal order={mapOrder} driverLoc={driverLoc} driverTrail={driverTrail} driverName={driverName} onClose={()=>setMapOrder(null)}/>}
    </div>
  );
}